import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { CheckoutPage } from '@liveauctioneers/hammer-ui-payments/checkout';
import { useHammerCheckoutData } from '@liveauctioneers/hammer-ui-payments/hooks';
import { Address, MarketplaceProps, ShippingMethod } from '@liveauctioneers/hammer-ui-payments/types';
import { useMemo } from 'react';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderDataIfNeeded } from '@/redux/modules/account/user/user.actions';
import { getUserData, getUserFirstName, getUserLastName } from '@/redux/modules/account/user/user.selectors';
import { useTrackPayNowClicked } from '@/utils/amplitude/analytics';
import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';

import { useGetMarketplaceProps } from './hooks/useGetMarketplaceProps';

type Props = {
    setMarketplaceProps: (marketplaceProps: MarketplaceProps) => void;
};

const CheckoutPageATGBody = ({ setMarketplaceProps }: Props) => {
    const { auctionId } = useHammerCheckoutData();
    const dispatch = useAppDispatch();

    // region checkout page analytics callbacks
    const trackCTAClickedAnalytics = useTrackCTAClickedAnalytics();
    const trackPayNowClicked = useTrackPayNowClicked();
    // endregion checkout page analytics callbacks

    // ADDRESS and ACH

    const firstName = useAppSelector(getUserFirstName);
    const lastName = useAppSelector(getUserLastName);
    const { addresses } = useAppSelector(getUserData);

    useGetMarketplaceProps({
        setMarketplaceProps,
    });

    const formattedAddresses = useMemo(() => {
        return addresses.map((address) => {
            return {
                address1: address.address1,
                address2: address.address2,
                address3: address.address3,
                city: address.city,
                countryCode: address.countryCode,
                firstName: address.firstName,
                lastName: address.lastName,
                organizationName: address.organizationName,
                phoneNumber: address.phoneNumber,
                postalCode: address.postalCode,
                state: address.state,
            } as Address;
        });
    }, [addresses]);

    if (!auctionId) {
        return (
            <StyledLoading>
                <Loading size={32} />
            </StyledLoading>
        );
    }

    return (
        <StyledCheckoutPageV2Body>
            <CheckoutPage
                addressList={formattedAddresses}
                analyticsTracking={{
                    amplitude: {
                        trackContinueToPaymentClicked: () =>
                            trackCTAClickedAnalytics(AnalyticsCTAEvent.CheckoutPage.SaveShippingMethod),
                        trackContinueToReviewClicked: () =>
                            trackCTAClickedAnalytics(AnalyticsCTAEvent.CheckoutPage.SavePaymentMethod),
                        trackContinueToShippingMethodClicked: () =>
                            trackCTAClickedAnalytics(AnalyticsCTAEvent.CheckoutPage.SaveShippingAddress),
                        trackPayNowClicked: (shippingMethod: ShippingMethod) => trackPayNowClicked({ shippingMethod }),
                    },
                }}
                name={{
                    first: firstName,
                    last: lastName,
                }}
                onAddressAddedCallback={() => dispatch(fetchBidderDataIfNeeded({ force: true }))}
            />
        </StyledCheckoutPageV2Body>
    );
};

export default CheckoutPageATGBody;

const StyledCheckoutPageV2Body = styled.div``;

const StyledLoading = styled.div`
    width: 100%;
    margin: 200px auto;
    display: flex;
    justify-content: center;
`;
